import {Segment} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useState} from 'react';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {RentalCalendarPeriod} from '../types/RentalCalendarPeriod';
import {RentalCalendarBlockDialog} from './RentalCalendarBlockDialog';
import {RentalCalendarCreateDialog} from './RentalCalendarCreateDialog';

interface RentalCalendarSlotDialogProps extends RequiredTestIdProps {
  vehicleId: string;
  vehicleTitle: string;
  slotStart: Date;
  slotEnd: Date;
  onRentalOrderCreated: (rentalOrderId: string) => void;
}

export function RentalCalendarSlotDialog(props: RentalCalendarSlotDialogProps) {
  // State is lifted here because switching the segment unmounts the other form, and a customer
  // from `CustomerMatchOrCreate` may already exist server-side, so losing it risks a duplicate
  const [target, setTarget] = useState<SlotTarget>('rentalOrder');
  const [vehicleId, setVehicleId] = useState(props.vehicleId);

  // A picked vehicle comes out of the fleet list, so reusing this title there would name another car
  const vehicleTitle = vehicleId === props.vehicleId ? props.vehicleTitle : undefined;
  const [period, setPeriod] = useState<RentalCalendarPeriod>({
    from: props.slotStart,
    to: props.slotEnd,
  });
  const [customer, setCustomer] = useState<CustomerResponseBodyV2 | null>(null);

  return (
    <VStack spacing={4}>
      <Segment<SlotTarget>
        value={target}
        onChange={setTarget}
        options={[
          {value: 'rentalOrder', label: i18n.t('entity.rentalOrder.labels.rentalOrder')},
          {value: 'vehicleBlock', label: i18n.t('entity.rental.labels.vehicleBlock')},
        ]}
        data-testid={suffixTestId('slotDialog-target', props)}
      />
      {target === 'rentalOrder' ? (
        <RentalCalendarCreateDialog
          data-testid={props['data-testid']}
          vehicleId={vehicleId}
          vehicleTitle={vehicleTitle}
          slotStart={period.from ?? props.slotStart}
          slotEnd={period.to ?? props.slotEnd}
          initialCustomer={customer}
          onCustomerChange={setCustomer}
          onPeriodChange={setPeriod}
          onVehicleChange={setVehicleId}
          onCreated={props.onRentalOrderCreated}
        />
      ) : (
        <RentalCalendarBlockDialog
          data-testid={props['data-testid']}
          vehicleId={vehicleId}
          vehicleTitle={vehicleTitle}
          slotStart={period.from}
          slotEnd={period.to}
          onPeriodChange={setPeriod}
          onVehicleChange={setVehicleId}
        />
      )}
    </VStack>
  );
}

type SlotTarget = 'rentalOrder' | 'vehicleBlock';
